import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, useToast } from './context/ToastContext';
import ToastContainer from './components/ToastContainer';
import LoadingPage from './pages/LoadingPage';
import { setApiErrorCallback } from './services/api';
import { theme } from './theme';

import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import RegisterCustomerPage from './pages/auth/RegisterCustomerPage';
import RegisterContractorPage from './pages/auth/RegisterContractorPage';
import LandingPage from './pages/LandingPage';
import SubmitInquiryPage from './pages/SubmitInquiryPage';
import CustomerJobsPage from './pages/customer/MyJobsPage';
import CustomerInquiriesPage from './pages/customer/InquiriesPage';
import ContractorBrowseJobsPage from './pages/contractor/BrowseJobsPage';
import ContractorMyLeadsPage from './pages/contractor/MyLeadsPage';
import ContractorInquiryDetailPage from './pages/contractor/InquiryDetailPage';
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminUserManagementPage from './pages/admin/UserManagementPage';
import AdminContentManagementPage from './pages/admin/ContentManagementPage';
import ContractorApprovalPage from './pages/admin/ContractorApprovalPage';
import Dashboard from './pages/DashboardPage';
import ImprintPage from './pages/legal/ImprintPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import TermsPage from './pages/legal/TermsPage';
import ContactPage from './pages/legal/ContactPage';
import NotFoundPage from './pages/errors/NotFoundPage';
import InternalErrorPage from './pages/errors/InternalErrorPage';
import AccessDeniedPage from './pages/errors/AccessDeniedPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import ErrorPageEditorPage from './pages/admin/ErrorPageEditorPage';
import ProfilePage from './pages/ProfilePage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminCallbacksPage from './pages/admin/CallbacksPage';
import AdminInquiriesPage from './pages/admin/InquiriesPage';
import AdminFormConfigPage from './pages/admin/FormConfigPage';
import AdminSettingsPage from './pages/admin/SettingsPage';
import DienstleisterPage from './pages/DienstleisterPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();

  useEffect(() => {
    setApiErrorCallback((message) => {
      addToast(message, 'error');
    });
  }, [addToast]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/customer" element={<RegisterCustomerPage />} />
      <Route path="/register/contractor" element={<RegisterContractorPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/submit-inquiry" element={<SubmitInquiryPage />} />
      <Route path="/dienstleister" element={<DienstleisterPage />} />

      <Route
        path="/customer/my-jobs"
        element={
          <ProtectedRoute requiredRole="customer">
            <CustomerJobsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customer/inquiries"
        element={
          <ProtectedRoute requiredRole="customer">
            <CustomerInquiriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contractor/browse-jobs"
        element={
          <ProtectedRoute requiredRole="contractor">
            <ContractorBrowseJobsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contractor/my-leads"
        element={
          <ProtectedRoute requiredRole="contractor">
            <ContractorMyLeadsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contractor/inquiry/:id"
        element={
          <ProtectedRoute requiredRole="contractor">
            <ContractorInquiryDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminUserManagementPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/contractors"
        element={
          <ProtectedRoute requiredRole="admin">
            <ContractorApprovalPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/content"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminContentManagementPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/error-pages"
        element={
          <ProtectedRoute requiredRole="admin">
            <ErrorPageEditorPage />
          </ProtectedRoute>
        }
      />

      <Route path="/imprint" element={<ImprintPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />

      <Route
        path="/account-settings"
        element={
          <ProtectedRoute>
            <AccountSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminPaymentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/callbacks"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminCallbacksPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/inquiries"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminInquiriesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/form-config"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminFormConfigPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />

      <Route path="/error/500" element={<InternalErrorPage />} />
      <Route path="/error/403" element={<AccessDeniedPage />} />

      <Route
        path="/dashboard"
        element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />}
      />
      <Route path="/unauthorized" element={<AccessDeniedPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
            <ToastContainer />
          </AuthProvider>
        </ToastProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
